import type {
  ActivityType,
  ApiToken,
  ApiTokenWithSecret,
  AuthConfigResponse,
  AuthMeResponse,
  AssignBoardOrgRequest,
  Attachment,
  AvatarMap,
  ChangePasswordRequest,
  Board,
  BoardRole,
  Card,
  CardAssignee,
  CardLink,
  Checklist,
  ChecklistItem,
  CreateBoardRequest,
  CreateCardLinkRequest,
  CreateCardRequest,
  CreateChecklistItemRequest,
  CreateChecklistRequest,
  CreateCommentRequest,
  CreateLabelRequest,
  CreateListRequest,
  CreateOrgRequest,
  CreateServiceUserRequest,
  CreateTokenRequest,
  TrustedIdentity,
  InviteMemberRequest,
  Label,
  List,
  LoginRequest,
  Member,
  MemberRole,
  MoveCardRequest,
  MoveListCardsRequest,
  BulkCardsResult,
  BoardField,
  CreateFieldRequest,
  FieldValue,
  FieldValues,
  UpdateFieldRequest,
  Org,
  RegisterRequest,
  ReorderListRequest,
  ServiceUser,
  ServiceUserWithTokens,
  TokenRoleCap,
  UpdateBoardRequest,
  UpdateCardRequest,
  UpdateChecklistItemRequest,
  UpdateChecklistRequest,
  UpdateLabelRequest,
  UpdateListRequest,
  UpdateProfileRequest,
} from "@kandoo/shared";

/** What the OAuth consent page shows (GET /api/oauth/consent). */
export interface OAuthConsentInfo {
  client: { name: string | null };
  /** Where the browser goes after approval — shown so the user can check it. */
  redirectHost: string;
  /** Who the connection may act as: the user, then their service users. */
  identities: {
    id: string;
    username: string;
    displayName: string | null;
    avatar: string | null;
    kind: "self" | "service";
  }[];
}

export interface BoardDetail extends Board {
  /** The requesting user's effective role on this board. */
  role: BoardRole;
  /** The board's label palette. */
  labels: Label[];
  /** The board's custom field definitions, in display order. */
  fields: BoardField[];
  lists: ListWithCards[];
}
export interface ListWithCards extends List {
  cards: BoardCard[];
}
/** A card as it appears on the board, enriched with checklist rollups + labels. */
export interface BoardCard extends Card {
  checklistTotal: number;
  checklistDone: number;
  labels: Label[];
  assignees: CardAssignee[];
  attachmentCount: number;
  commentCount: number;
  /** Id of the card's first image attachment, for board-face previews. */
  previewAttachmentId: string | null;
  /** fieldId → value for the board's custom fields (unset ones absent). */
  fieldValues: FieldValues;
}
/** An archived card as returned by the board archive listing. */
export interface ArchivedCard extends Card {
  listTitle: string;
}

export interface CommentView {
  id: string;
  cardId: string;
  userId: string;
  authorUsername: string;
  authorDisplayName: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
}
export interface ActivityView {
  id: string;
  cardId: string;
  userId: string | null;
  actorUsername: string | null;
  actorDisplayName: string | null;
  type: ActivityType;
  data: Record<string, unknown> | null;
  createdAt: string;
}
/** An activity row in the board-wide feed, carrying its card's id + title. */
export interface BoardActivityView extends ActivityView {
  cardId: string;
  cardTitle: string;
}
export interface CardDetail extends Card {
  checklists: ChecklistWithItems[];
  links: { parents: CardLink[]; children: CardLink[] };
  labels: Label[];
  fieldValues: FieldValues;
  assignees: CardAssignee[];
  comments: CommentView[];
  activity: ActivityView[];
  attachments: Attachment[];
}
export interface ChecklistWithItems extends Checklist {
  items: ChecklistItem[];
}

export type NotificationType = "mention" | "assigned";
/** Carries the card title + board id so the bell can render and deep-link. */
export interface NotificationView {
  id: string;
  userId: string;
  actorId: string | null;
  actorUsername: string | null;
  actorDisplayName: string | null;
  type: NotificationType;
  cardId: string;
  cardTitle: string;
  boardId: string;
  commentId: string | null;
  data: { excerpt?: string } | null;
  readAt: string | null;
  createdAt: string;
}

export interface OrgSummary extends Org {
  role: BoardRole;
}
export interface OrgDetail extends Org {
  role: BoardRole;
  members: Member[];
}

export interface ApiError {
  error: { code: string; message: string };
}

/**
 * JSON requests give up after this long. Without a cap, a request sent down a
 * keep-alive socket that died while the laptop slept (or a NAT dropped it) can
 * hang for minutes — and React Query won't start the next poll while one is
 * still in flight, so the board silently stops updating. Uploads are exempt.
 */
const REQUEST_TIMEOUT_MS = 20_000;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      credentials: "same-origin",
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (err) {
    if ((err as Error)?.name === "TimeoutError") {
      throw Object.assign(new Error("The server took too long to respond"), {
        status: 0,
        code: "timeout",
      });
    }
    throw err;
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const err = data as ApiError | undefined;
    const message = err?.error?.message ?? `Request failed: ${res.status}`;
    // `body` keeps the whole response for endpoints that put more than the
    // error envelope in it (the OAuth consent API's `redirect`).
    const e: Error & { status: number; code: string; body: unknown } = Object.assign(
      new Error(message),
      { status: res.status, code: err?.error?.code ?? "request_failed", body: data },
    );
    throw e;
  }
  return data as T;
}

/* -------------------------------------------------------------------------- */
/* Auth                                                                        */
/* -------------------------------------------------------------------------- */

export const api = {
  auth: {
    register: (b: RegisterRequest) =>
      request<AuthMeResponse>("POST", "/auth/register", b),
    login: (b: LoginRequest) =>
      request<AuthMeResponse>("POST", "/auth/login", b),
    logout: () => request<void>("POST", "/auth/logout"),
    logoutAll: () => request<void>("POST", "/auth/logout-all"),
    me: () => request<AuthMeResponse>("GET", "/auth/me"),
    config: () => request<AuthConfigResponse>("GET", "/auth/config"),
    updateMe: (b: UpdateProfileRequest) =>
      request<AuthMeResponse>("PATCH", "/auth/me", b),
    changePassword: (b: ChangePasswordRequest) =>
      request<void>("POST", "/auth/change-password", b),
  },

  users: {
    /** userId → avatar value, for users who've chosen a non-default avatar. */
    avatars: () => request<AvatarMap>("GET", "/users/avatars"),
  },

  oauth: {
    /** Validate an authorization request (the consent page's query string). */
    consentInfo: (search: string) =>
      request<OAuthConsentInfo>("GET", `/oauth/consent${search}`),
    /** Approve or deny it; resolves to where the browser should go next. */
    consent: (b: {
      params: Record<string, string>;
      approve: boolean;
      actAs?: string;
      roleCap?: TokenRoleCap | "";
    }) => request<{ redirect: string }>("POST", "/oauth/consent", b),
  },

  tokens: {
    list: () => request<ApiToken[]>("GET", "/tokens"),
    create: (b: CreateTokenRequest) =>
      request<ApiTokenWithSecret>("POST", "/tokens", b),
    revoke: (id: string) => request<void>("DELETE", `/tokens/${id}`),
  },

  serviceUsers: {
    list: () => request<ServiceUserWithTokens[]>("GET", "/service-users"),
    create: (b: CreateServiceUserRequest) =>
      request<ServiceUser & { tokens: ApiToken[] }>(
        "POST",
        "/service-users",
        b,
      ),
    delete: (id: string) => request<void>("DELETE", `/service-users/${id}`),
    updateAvatar: (id: string, avatar: string | null) =>
      request<ServiceUser>("PATCH", `/service-users/${id}`, { avatar }),
    mintToken: (id: string, b: CreateTokenRequest) =>
      request<ApiTokenWithSecret>("POST", `/service-users/${id}/tokens`, b),
    revokeToken: (id: string, tokenId: string) =>
      request<void>("DELETE", `/service-users/${id}/tokens/${tokenId}`),
  },

  boards: {
    list: () => request<Board[]>("GET", "/boards"),
    create: (b: CreateBoardRequest) => request<Board>("POST", "/boards", b),
    get: (id: string) => request<BoardDetail>("GET", `/boards/${id}`),
    /** cardId → concatenated comment text, for the board search. Loaded lazily. */
    commentIndex: (id: string) =>
      request<Record<string, string>>("GET", `/boards/${id}/comment-index`),
    update: (id: string, b: UpdateBoardRequest) =>
      request<Board>("PATCH", `/boards/${id}`, b),
    delete: (id: string) => request<void>("DELETE", `/boards/${id}`),
    archivedCards: (id: string) =>
      request<ArchivedCard[]>("GET", `/boards/${id}/archived-cards`),
    activity: (id: string) =>
      request<BoardActivityView[]>("GET", `/boards/${id}/activity`),
    star: (id: string) => request<void>("PUT", `/boards/${id}/star`),
    unstar: (id: string) => request<void>("DELETE", `/boards/${id}/star`),
  },

  lists: {
    list: (boardId: string) =>
      request<List[]>("GET", `/boards/${boardId}/lists`),
    create: (boardId: string, b: CreateListRequest) =>
      request<List>("POST", `/boards/${boardId}/lists`, b),
    update: (id: string, b: UpdateListRequest) =>
      request<List>("PATCH", `/lists/${id}`, b),
    reorder: (id: string, b: ReorderListRequest) =>
      request<List>("POST", `/lists/${id}/reorder`, b),
    /** Archive every card on this list's face; returns how many were archived. */
    archiveCards: (id: string) =>
      request<BulkCardsResult>("POST", `/lists/${id}/archive-cards`),
    /** Move every card on this list's face to another list. */
    moveCards: (id: string, b: MoveListCardsRequest) =>
      request<BulkCardsResult>("POST", `/lists/${id}/move-cards`, b),
    delete: (id: string) => request<void>("DELETE", `/lists/${id}`),
  },

  cards: {
    list: (listId: string) => request<Card[]>("GET", `/lists/${listId}/cards`),
    create: (listId: string, b: CreateCardRequest) =>
      request<Card>("POST", `/lists/${listId}/cards`, b),
    get: (id: string) => request<CardDetail>("GET", `/cards/${id}`),
    update: (id: string, b: UpdateCardRequest) =>
      request<Card>("PATCH", `/cards/${id}`, b),
    move: (id: string, b: MoveCardRequest) =>
      request<Card>("POST", `/cards/${id}/move`, b),
    archive: (id: string) => request<Card>("POST", `/cards/${id}/archive`),
    unarchive: (id: string) => request<Card>("POST", `/cards/${id}/unarchive`),
    delete: (id: string) => request<void>("DELETE", `/cards/${id}`),
  },

  checklists: {
    list: (cardId: string) =>
      request<ChecklistWithItems[]>("GET", `/cards/${cardId}/checklists`),
    create: (cardId: string, b: CreateChecklistRequest) =>
      request<Checklist>("POST", `/cards/${cardId}/checklists`, b),
    update: (id: string, b: UpdateChecklistRequest) =>
      request<Checklist>("PATCH", `/checklists/${id}`, b),
    delete: (id: string) => request<void>("DELETE", `/checklists/${id}`),
    addItem: (checklistId: string, b: CreateChecklistItemRequest) =>
      request<ChecklistItem>("POST", `/checklists/${checklistId}/items`, b),
    updateItem: (id: string, b: UpdateChecklistItemRequest) =>
      request<ChecklistItem>("PATCH", `/checklist-items/${id}`, b),
    deleteItem: (id: string) =>
      request<void>("DELETE", `/checklist-items/${id}`),
  },

  links: {
    list: (cardId: string) =>
      request<{ parents: CardLink[]; children: CardLink[] }>(
        "GET",
        `/cards/${cardId}/links`,
      ),
    create: (cardId: string, b: CreateCardLinkRequest) =>
      request<CardLink>("POST", `/cards/${cardId}/links`, b),
    delete: (id: string) => request<void>("DELETE", `/links/${id}`),
  },

  labels: {
    list: (boardId: string) =>
      request<Label[]>("GET", `/boards/${boardId}/labels`),
    create: (boardId: string, b: CreateLabelRequest) =>
      request<Label>("POST", `/boards/${boardId}/labels`, b),
    update: (id: string, b: UpdateLabelRequest) =>
      request<Label>("PATCH", `/labels/${id}`, b),
    delete: (id: string) => request<void>("DELETE", `/labels/${id}`),
    assign: (cardId: string, labelId: string) =>
      request<void>("PUT", `/cards/${cardId}/labels/${labelId}`),
    unassign: (cardId: string, labelId: string) =>
      request<void>("DELETE", `/cards/${cardId}/labels/${labelId}`),
  },

  fields: {
    create: (boardId: string, b: CreateFieldRequest) =>
      request<BoardField>("POST", `/boards/${boardId}/fields`, b),
    update: (id: string, b: UpdateFieldRequest) =>
      request<BoardField>("PATCH", `/fields/${id}`, b),
    delete: (id: string) => request<void>("DELETE", `/fields/${id}`),
    /** null (or "") clears the value. */
    setValue: (cardId: string, fieldId: string, value: FieldValue | null) =>
      request<{ fieldId: string; value: FieldValue | null }>(
        "PUT",
        `/cards/${cardId}/fields/${fieldId}`,
        { value },
      ),
  },

  orgs: {
    list: () => request<OrgSummary[]>("GET", "/orgs"),
    create: (b: CreateOrgRequest) => request<Org>("POST", "/orgs", b),
    get: (id: string) => request<OrgDetail>("GET", `/orgs/${id}`),
    delete: (id: string) => request<void>("DELETE", `/orgs/${id}`),
    invite: (id: string, b: InviteMemberRequest) =>
      request<Member[]>("POST", `/orgs/${id}/members`, b),
    setRole: (id: string, userId: string, role: MemberRole) =>
      request<Member[]>("PATCH", `/orgs/${id}/members/${userId}`, { role }),
    removeMember: (id: string, userId: string) =>
      request<void>("DELETE", `/orgs/${id}/members/${userId}`),
  },

  assignees: {
    assign: (cardId: string, userId: string) =>
      request<CardAssignee[]>("PUT", `/cards/${cardId}/assignees/${userId}`),
    unassign: (cardId: string, userId: string) =>
      request<void>("DELETE", `/cards/${cardId}/assignees/${userId}`),
  },

  comments: {
    create: (cardId: string, b: CreateCommentRequest) =>
      request<CommentView>("POST", `/cards/${cardId}/comments`, b),
    update: (id: string, body: string) =>
      request<CommentView>("PATCH", `/comments/${id}`, { body }),
    delete: (id: string) => request<void>("DELETE", `/comments/${id}`),
  },

  attachments: {
    upload: async (cardId: string, file: File): Promise<Attachment> => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/cards/${cardId}/attachments`, {
        method: "POST",
        credentials: "same-origin",
        body: fd,
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : undefined;
      if (!res.ok) {
        const err = data as ApiError | undefined;
        throw Object.assign(
          new Error(err?.error?.message ?? `Upload failed: ${res.status}`),
          { status: res.status, code: err?.error?.code ?? "upload_failed" },
        );
      }
      return data as Attachment;
    },
    delete: (id: string) => request<void>("DELETE", `/attachments/${id}`),
  },

  members: {
    list: (boardId: string) =>
      request<Member[]>("GET", `/boards/${boardId}/members`),
    invite: (boardId: string, b: InviteMemberRequest) =>
      request<Member[]>("POST", `/boards/${boardId}/members`, b),
    setRole: (boardId: string, userId: string, role: MemberRole) =>
      request<Member[]>("PATCH", `/boards/${boardId}/members/${userId}`, {
        role,
      }),
    remove: (boardId: string, userId: string) =>
      request<void>("DELETE", `/boards/${boardId}/members/${userId}`),
    assignOrg: (boardId: string, b: AssignBoardOrgRequest) =>
      request<Board>("PUT", `/boards/${boardId}/org`, b),
  },

  /** Trusted identities for a service user's token (see TrustedIdentities UI). */
  trusted: {
    list: (tokenId: string) =>
      request<TrustedIdentity[]>("GET", `/tokens/${tokenId}/trusted`),
    add: (tokenId: string, username: string) =>
      request<TrustedIdentity[]>("POST", `/tokens/${tokenId}/trusted`, {
        username,
      }),
    remove: (tokenId: string, userId: string) =>
      request<void>("DELETE", `/tokens/${tokenId}/trusted/${userId}`),
  },

  notifications: {
    list: (unreadOnly = false) =>
      request<NotificationView[]>(
        "GET",
        `/notifications${unreadOnly ? "?unread=1" : ""}`,
      ),
    unreadCount: () =>
      request<{ count: number }>("GET", "/notifications/unread_count"),
    markRead: (id: string) =>
      request<void>("POST", `/notifications/${id}/read`),
    markAllRead: () =>
      request<{ marked: number }>("POST", "/notifications/read_all"),
  },
};

/** URL to view/preview an attachment (images render inline). */
export const attachmentUrl = (id: string) => `/api/attachments/${id}`;
/** URL that forces a download of an attachment. */
export const attachmentDownloadUrl = (id: string) =>
  `/api/attachments/${id}?download=1`;